import { AUTH0_LOGOUT_GENERATION_STORAGE_KEY } from '../constants/localStorage';
import { invalidateAuthSessionEpoch } from './authSessionEpoch';

/**
 * Same-document flag for an explicit Auth0 `/v2/logout` that has started but
 * has not yet navigated away.
 *
 * Inactivity keeps the durable `auth0Reauth` cookie so a later load cannot
 * silently restore SSO. Voluntary logout must not: after Auth0 ends the tenant
 * session, a sibling-app login (SBW) should be able to SSO back into eComm.
 *
 * The flag blocks this document. `markAuth0LogoutInFlight` also bumps a
 * localStorage generation so another tab can discard an in-flight token write.
 * That tab must already be listening: `watchAuth0LogoutAcrossTabs` installs the
 * listener. A generation already in storage is not logout on a later page load,
 * so a sibling-app login can still silent-SSO.
 */
let auth0LogoutInFlight = false;
let inMemoryLogoutGeneration = 0;
let logoutStorageListenerTarget: Window | undefined;

type LogoutStorageEvent = {
    key: string | null;
    newValue: string | null;
};

function readStoredLogoutGeneration(): number | undefined {
    try {
        const raw = globalThis.localStorage?.getItem(AUTH0_LOGOUT_GENERATION_STORAGE_KEY);
        if (!raw) {
            return undefined;
        }

        const parsed = Number.parseInt(raw, 10);
        return Number.isFinite(parsed) ? parsed : undefined;
    } catch {
        return undefined;
    }
}

function writeLogoutGeneration(generation: number): void {
    inMemoryLogoutGeneration = generation;

    try {
        globalThis.localStorage?.setItem(AUTH0_LOGOUT_GENERATION_STORAGE_KEY, String(generation));
    } catch {
        // Private browsing can reject storage. This document still has the in-memory flag.
    }
}

function noteCrossTabAuth0Logout(): void {
    auth0LogoutInFlight = true;
    invalidateAuthSessionEpoch();
}

function readLogoutStorageEvent(event: Event): LogoutStorageEvent | undefined {
    if (!('key' in event) || !('newValue' in event)) {
        return undefined;
    }

    const key = event.key;
    const newValue = event.newValue;
    if (typeof key !== 'string' && key !== null) {
        return undefined;
    }
    if (typeof newValue !== 'string' && newValue !== null) {
        return undefined;
    }

    return { key, newValue };
}

function onAuth0LogoutStorage(event: Event): void {
    const storageEvent = readLogoutStorageEvent(event);
    if (storageEvent?.key !== AUTH0_LOGOUT_GENERATION_STORAGE_KEY || storageEvent?.newValue == null) {
        return;
    }

    noteCrossTabAuth0Logout();
}

function installAuth0LogoutStorageListener(): void {
    const win = globalThis.window;
    if (!win?.addEventListener || logoutStorageListenerTarget === win) {
        return;
    }

    logoutStorageListenerTarget = win;
    win.addEventListener('storage', onAuth0LogoutStorage);
}

/** Subscribe to logout bumps from other tabs. Safe to call more than once. */
export function watchAuth0LogoutAcrossTabs(): void {
    installAuth0LogoutStorageListener();
}

export function getAuth0LogoutGeneration(): number {
    watchAuth0LogoutAcrossTabs();
    return readStoredLogoutGeneration() ?? inMemoryLogoutGeneration;
}

export function markAuth0LogoutInFlight() {
    watchAuth0LogoutAcrossTabs();
    auth0LogoutInFlight = true;
    const nextGeneration = (readStoredLogoutGeneration() ?? inMemoryLogoutGeneration) + 1;
    writeLogoutGeneration(nextGeneration);
}

export function isAuth0LogoutInFlight() {
    watchAuth0LogoutAcrossTabs();
    return auth0LogoutInFlight;
}

export function resetAuth0LogoutInFlight() {
    auth0LogoutInFlight = false;
    inMemoryLogoutGeneration = 0;

    try {
        globalThis.localStorage?.removeItem(AUTH0_LOGOUT_GENERATION_STORAGE_KEY);
    } catch {
        // Storage can throw in private browsing; the in-memory flag is already clear.
    }
}
